// CoachBlog.jsx - Índice del blog B2B para captación de coaches (Wayo Coach).
// Reutiliza Blog.css pero usa el header de la landing de coach y CTA a /coach/register.
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CoachLandingHeader from "../components/coach/CoachLandingHeader";
import Footer from "../components/Footer";
import { coachBlogPosts, coachCategories, coachPopularTags } from "../data/coachBlogPosts";
import { trackCoachCtaClick } from "../analytics/googleAnalytics";
import "./Blog.css";
import "./coach-blog.css";

export default function CoachBlog() {
  const [searchParams] = useSearchParams();
  const tagFromUrl = searchParams.get("tag") || "";
  const isTagPage = Boolean(tagFromUrl);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState(tagFromUrl);

  const filteredPosts = coachBlogPosts.filter((post) => {
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    const matchesSearch =
      !searchTerm ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts[0];
  const otherPosts = filteredPosts.slice(1);

  return (
    <>
      <Helmet>
        <title>Blog para Entrenadores | Wayo Coach - Capta Clientes y Gestiona tu Negocio</title>
        <meta
          name="description"
          content="Guías para entrenadores personales, fisios y coaches online: cómo captar clientes, cuánto cobrar, software gratis para gestionar clientes y alternativas a Harbiz."
        />
        <meta
          name="keywords"
          content="blog entrenadores, captar clientes fitness, software para entrenadores, cuánto cobrar entrenador online, marketing para coaches"
        />
        <link rel="canonical" href="https://wayoai.app/blog-entrenadores" />
        <meta
          name="robots"
          content={isTagPage ? "noindex, follow" : "index, follow, max-image-preview:large"}
        />
        <meta property="og:title" content="Blog para Entrenadores | Wayo Coach" />
        <meta
          property="og:description"
          content="Capta más clientes, cobra lo que vales y gestiona tu negocio de coaching. Recursos gratis para entrenadores y coaches online."
        />
        <meta property="og:url" content="https://wayoai.app/blog-entrenadores" />
        <meta property="og:type" content="website" />
      </Helmet>

      <main className="blog-page coach-blog-scope">
        <CoachLandingHeader />

        <section className="blog-content">
          <div className="blog-hero">
            <div className="blog-hero-content">
              <h1 className="blog-hero-title">Blog para entrenadores</h1>
              <p className="blog-hero-subtitle">
                Cómo captar clientes, cuánto cobrar y qué herramientas usar para hacer crecer
                tu negocio de coaching
              </p>
            </div>
          </div>

          <div className="blog-container">
            {/* CTA a landings B2B (interlinkado SEO) */}
            <div
              className="blog-app-cta blog-app-cta-light"
              style={{
                marginBottom: "24px",
                background:
                  "linear-gradient(135deg, rgba(108,58,255,0.08) 0%, rgba(139,92,246,0.08) 100%)",
              }}
            >
              <div className="blog-app-cta-copy">
                <span className="blog-app-cta-badge">Empieza aquí</span>
                <h2>¿Buscas una plataforma gratis para gestionar a tus clientes?</h2>
                <p>
                  Rutinas, planes de nutrición con IA y seguimiento, sin cuota mensual ni límite
                  de clientes. Descubre por qué Wayo Coach es la alternativa gratuita a Harbiz.
                </p>
              </div>
              <div className="money-inline-links">
                <Link to="/coaches">Ver Wayo Coach, el software gratis para entrenadores</Link>
              </div>
            </div>

            {/* CTA principal: registro coach */}
            <div className="blog-app-cta" id="blog-coach-cta">
              <div className="blog-app-cta-copy">
                <span className="blog-app-cta-badge">100% Gratis</span>
                <h2>Gestiona a tus clientes con Wayo Coach</h2>
                <p>
                  Deja el Excel y el WhatsApp. Crea rutinas y dietas profesionales, haz
                  seguimiento del progreso y da un servicio premium a tus clientes. Sin cuota, sin
                  permanencia y sin límite de clientes.
                </p>
              </div>
              <div className="blog-app-cta-actions">
                <Link
                  to="/coach/register"
                  className="article-download-link"
                  onClick={() => trackCoachCtaClick("coach_blog_index_cta")}
                >
                  Unirme gratis
                </Link>
              </div>
            </div>

            {/* Buscador y filtros */}
            <div className="blog-filters">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="categories-filter">
                <button
                  className={`category-btn ${!selectedCategory ? "active" : ""}`}
                  onClick={() => setSelectedCategory(null)}
                >
                  Todos
                </button>
                {coachCategories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`category-btn ${selectedCategory === cat.name ? "active" : ""}`}
                    onClick={() => setSelectedCategory(cat.name)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="blog-layout">
              <div className="blog-main">
                {/* Post destacado */}
                {!searchTerm && !selectedCategory && featuredPost && (
                  <Link to={`/blog-entrenadores/${featuredPost.slug}`} className="featured-post">
                    <div className="featured-image-wrapper">
                      <img src={featuredPost.image} alt={featuredPost.title} />
                      <span className="featured-badge">Destacado</span>
                    </div>
                    <div className="featured-content">
                      <div className="post-meta">
                        <span className="category-badge">{featuredPost.category}</span>
                        <span className="post-date">
                          {new Date(featuredPost.date).toLocaleDateString("es-ES", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                        <span className="read-time">⏱️ {featuredPost.readTime}</span>
                      </div>
                      <h2 className="featured-title">{featuredPost.title}</h2>
                      <p className="featured-excerpt">{featuredPost.excerpt}</p>
                      <div className="post-footer">
                        <span className="post-author">Por {featuredPost.author}</span>
                        <span className="read-more">Leer más</span>
                      </div>
                    </div>
                  </Link>
                )}

                {/* Grid de posts */}
                <div className="posts-grid">
                  {filteredPosts.length > 0 ? (
                    (searchTerm || selectedCategory ? filteredPosts : otherPosts).map((post) => (
                      <Link
                        key={post.id}
                        to={`/blog-entrenadores/${post.slug}`}
                        className="post-card"
                      >
                        <div className="post-image">
                          <img src={post.image} alt={post.title} />
                          <span className="category-badge">{post.category}</span>
                        </div>
                        <div className="post-content">
                          <div className="post-meta-small">
                            <span>
                              {new Date(post.date).toLocaleDateString("es-ES", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </span>
                            <span>⏱️ {post.readTime}</span>
                          </div>
                          <h3 className="post-title">{post.title}</h3>
                          <p className="post-excerpt">{post.excerpt}</p>
                          <div className="post-tags">
                            {post.tags.slice(0, 3).map((tag, index) => (
                              <span key={index} className="tag">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <div className="no-results">
                      <p className="no-results-icon">📝</p>
                      <p className="no-results-text">No se encontraron artículos</p>
                      <button
                        className="clear-filters"
                        onClick={() => {
                          setSearchTerm("");
                          setSelectedCategory(null);
                        }}
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="blog-sidebar">
                <div className="sidebar-widget cta-widget">
                  <h3>🚀 Empieza gratis</h3>
                  <p>Gestiona a todos tus clientes sin pagar cuota mensual.</p>
                  <Link
                    to="/coach/register"
                    className="cta-btn"
                    onClick={() => trackCoachCtaClick("coach_blog_sidebar_cta")}
                  >
                    Unirme a Wayo Coach
                  </Link>
                </div>

                <div className="sidebar-widget">
                  <h3>🏷️ Tags Populares</h3>
                  <div className="tags-cloud">
                    {coachPopularTags.map((tag, index) => (
                      <button key={index} className="tag-btn" onClick={() => setSearchTerm(tag)}>
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
